import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme }    from "../context/ThemeContext";
import { useAuth }     from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { ShieldCheck, Clock, ExternalLink, RefreshCw, CheckCircle, Loader2 } from "lucide-react";

var PLAN_INFO = {
  tier_6: { name: "Harmony", price: 149 },
  tier_9: { name: "Power",   price: 499 },
};

var CURRENCIES = ["USDT", "BTC", "ETH"];

export default function PendingVerification() {
  var theme    = useTheme().theme;
  var dm       = useDarkMode();
  var isDark   = dm.isDark;
  var auth     = useAuth();
  var user     = auth.user;
  var navigate = useNavigate();
  var [searchParams] = useSearchParams();

  var [checking,   setChecking]   = useState(false);
  var [approved,   setApproved]   = useState(false);
  var [mounted,    setMounted]    = useState(false);
  var [paying,     setPaying]     = useState(false);
  var [payError,   setPayError]   = useState("");
  var [currency,   setCurrency]   = useState("USDT");

  var tier     = sessionStorage.getItem("gl_pending_tier") || (user && user.tier);
  var planInfo = (tier && PLAN_INFO[tier]) || { name: "Selected Plan", price: "—" };

  var pageBg   = isDark ? "#030303" : "#FAFAFA";
  var cardBg   = isDark ? "#060606" : "#FFFFFF";
  var border   = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  var textPrimary = isDark ? "#FFFFFF" : "#111111";
  var textMuted   = isDark ? "#71717A" : "#555555";

  useEffect(function () {
    var t = setTimeout(function () { setMounted(true); }, 60);
    return function () { clearTimeout(t); };
  }, []);

  // Redirect if already approved
  useEffect(function () {
    if (user && user.role === "client")   navigate("/dashboard");
    if (user && user.role === "investor") navigate("/investor");
  }, [user, navigate]);

  // Auto-check status if returning from NowPayments (paid=1 in URL)
  useEffect(function () {
    if (searchParams.get("paid") === "1") {
      checkStatus();
    }
  }, []);

  async function handlePay() {
    if (!tier || !PLAN_INFO[tier]) {
      setPayError("No plan selected. Go back and choose Harmony or Power.");
      return;
    }
    setPaying(true);
    setPayError("");
    try {
      var res = await fetch(
        (process.env.REACT_APP_BACKEND_URL || "") + "/api/payment/create-tier",
        {
          method:      "POST",
          credentials: "include",
          headers:     { "Content-Type": "application/json" },
          body: JSON.stringify({ tier: tier, currency: currency }),
        }
      );
      var data = await res.json();
      if (!res.ok) {
        setPayError(data.detail || "Payment creation failed. Try again.");
        setPaying(false);
        return;
      }
      // Redirect to NowPayments invoice — amount is pre-filled
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        setPayError("No invoice URL returned. Contact support@gaurilabs.com");
        setPaying(false);
      }
    } catch (e) {
      setPayError("Network error. Please try again.");
      setPaying(false);
    }
  }

  async function checkStatus() {
    setChecking(true);
    var result = await auth.refreshMe();
    setChecking(false);
    if (result && (result.role === "client" || result.role === "investor")) {
      setApproved(true);
      setTimeout(function () { navigate("/dashboard"); }, 2000);
    }
  }

  if (approved) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: pageBg }}>
        <div className="text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: theme.color }} />
          <h2 className="font-display font-black text-3xl" style={{ color: textPrimary }}>Account Approved!</h2>
          <p className="text-zinc-500 mt-2">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" style={{ background: pageBg }}>
      <div className="w-full max-w-lg transition-all duration-700"
        style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)" }}>

        <Link to="/" className="block text-center font-display font-black text-2xl tracking-tight mb-12"
          style={{ color: theme.color }}>GauriLabs</Link>

        {/* Status card */}
        <div className="border p-8 mb-5" style={{ background: cardBg, borderColor: theme.color + "40" }}>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 flex items-center justify-center border shrink-0"
              style={{ borderColor: theme.color + "40", background: theme.color + "10" }}>
              <Clock className="h-5 w-5" style={{ color: theme.color }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Account Status</div>
              <h2 className="font-display font-black text-xl" style={{ color: textPrimary }}>
                Pending Payment Verification
              </h2>
            </div>
          </div>

          {/* Plan */}
          <div className="p-4 border mb-6" style={{ borderColor: border, background: isDark ? "#030303" : "#F8F8F8" }}>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Plan Selected</div>
            <div className="flex items-center justify-between">
              <span className="font-display font-black text-xl" style={{ color: theme.color }}>{planInfo.name}</span>
              <span className="font-display font-bold text-lg" style={{ color: textPrimary }}>
                ${planInfo.price}<span className="text-sm font-normal text-zinc-500">/mo</span>
              </span>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-4 mb-8">
            {[
              { num: "1", title: "Choose payment currency", body: "Select USDT, BTC, or ETH below." },
              { num: "2", title: "Complete payment on NowPayments", body: "Click Pay — you will be redirected to a secure payment page with the exact amount pre-filled." },
              { num: "3", title: "Automatic activation", body: "Once blockchain confirms your payment, your account is activated instantly and you receive a confirmation email." },
            ].map(function (step) {
              return (
                <div key={step.num} className="flex gap-4">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-display font-black text-xs mt-0.5"
                    style={{ background: theme.color + "15", color: theme.color, border: "1px solid " + theme.color + "30" }}>
                    {step.num}
                  </div>
                  <div>
                    <div className="font-display font-bold text-sm mb-1" style={{ color: textPrimary }}>{step.title}</div>
                    <p className="text-xs leading-relaxed text-zinc-500">{step.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Currency selector */}
          <div className="mb-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Pay with</div>
            <div className="grid grid-cols-3 gap-2">
              {CURRENCIES.map(function (c) {
                var active = currency === c;
                return (
                  <button key={c} onClick={function () { setCurrency(c); }}
                    className="py-3 border font-display font-bold text-sm tracking-wide transition-all"
                    style={{
                      borderColor:     active ? theme.color : border,
                      color:           active ? theme.color : textMuted,
                      background:      active ? theme.color + "10" : "transparent",
                      boxShadow:       active ? "0 0 16px " + theme.color + "30" : "none",
                    }}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment reference */}
          {user && (
            <div className="p-3 border mb-5" style={{ borderColor: theme.color + "30", background: theme.color + "06" }}>
              <div className="text-xs uppercase tracking-widest mb-1" style={{ color: theme.color }}>Your account email</div>
              <div className="font-mono text-sm font-bold" style={{ color: textPrimary }}>{user.email}</div>
            </div>
          )}

          {/* Error */}
          {payError && (
            <div className="mb-4 px-4 py-3 border border-red-500/25 bg-red-500/5 text-red-400 text-sm">{payError}</div>
          )}

          {/* Pay button */}
          <button onClick={handlePay} disabled={paying}
            className="flex items-center justify-center gap-3 w-full py-4 font-display font-bold text-sm tracking-widest uppercase transition-all hover:scale-[1.02] mb-4 disabled:opacity-60"
            style={{ backgroundColor: theme.color, color: "#000", boxShadow: "0 0 32px " + theme.color + "44" }}>
            {paying
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating payment...</>
              : <><ExternalLink className="h-4 w-4" /> Pay ${planInfo.price} {currency} via NowPayments</>
            }
          </button>

          {/* Check status */}
          <button onClick={checkStatus} disabled={checking}
            className="flex items-center justify-center gap-2 w-full py-3.5 font-display font-bold text-sm tracking-widest uppercase border transition-all disabled:opacity-50"
            style={{ borderColor: border, color: textMuted }}>
            <RefreshCw className={"h-4 w-4 " + (checking ? "animate-spin" : "")} />
            {checking ? "Checking..." : "I've paid — check my status"}
          </button>
        </div>

        {/* Support */}
        <div className="border p-5 text-center" style={{ borderColor: border, background: cardBg }}>
          <ShieldCheck className="h-5 w-5 mx-auto mb-2" style={{ color: theme.color }} />
          <p className="text-xs text-zinc-500 leading-relaxed">
            Payments confirm automatically via blockchain. Usually within 10–30 minutes.
            Questions? <span style={{ color: theme.color }}>support@gaurilabs.com</span>
          </p>
        </div>

        <div className="mt-6 text-center">
          <button onClick={function () { auth.logout().then(function () { navigate("/"); }); }}
            className="text-xs text-zinc-500 hover:text-zinc-400 transition-colors font-mono">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
